import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCheck,   FaCalendar, FaFileSignature, FaClock, FaMapMarkerAlt, FaUsers, FaShieldAlt,  FaDollarSign } from "react-icons/fa";
import { useToast } from "../../Context/ToastContext";
import { AuthContext } from "../../Context/AuthContext";
import useReadCharterRequest from "../Libs/useReadCharterRequest";
import useCharterResponse from "../Libs/useCharterResponse";
import useSelectMeetingDate from "../Libs/useSelectMeetingDate";
import useSignVesselNominationAgreement from "../Libs/useSignVesselNominationAgreement";
import useSignCharterPartyAgreement from "../Libs/useSignCharterPartyAgreement";
import Skeleton from "react-loading-skeleton";
import WorkflowSteps from "../../Utils/WorkflowSteps";
import { calculateWorkflowStep, getNextAction } from "../../Utils/workflowStepCalculator";
import VesselNominationAgreementModal from "../../Charterer/components/VesselNominationAgreementModal";
import CharterPartyAgreementModal from "../../Charterer/components/CharterPartyAgreementModal";
import "./CharterRequestDetails.css";

const CharterRequestDetails = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { profileData } = useContext(AuthContext);

  // Modal states
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showMeetingDateModal, setShowMeetingDateModal] = useState(false);
  const [showSignVesselModal, setShowSignVesselModal] = useState(false);
  const [showSignCharterPartyModal, setShowSignCharterPartyModal] = useState(false);

  // Form states
  const [responseForm, setResponseForm] = useState({
    canProvideSecurity: {
      processed: true,
      pricing: ""
    },
    vesselOwnerResponse: {
      processed: true,
      userMessage: ""
    }
  });
  const [meetingDates, setMeetingDates] = useState({
    date1: "",
    date2: ""
  });

  // API hooks
  const { charterRequest, charterRequestLoading, charterRequestError } = useReadCharterRequest(requestId);
  const { sendCharterResponse, isLoading: responseLoading, isSuccess: responseSuccess, isError: responseError, error: responseErrorData } = useCharterResponse();
  const { selectMeetingDate, isLoading: meetingDateLoading, isSuccess: meetingDateSuccess, isError: meetingDateError, error: meetingDateErrorData } = useSelectMeetingDate();
  const { signAgreement: signVesselAgreement, isLoading: signVesselLoading, isSuccess: signVesselSuccess, isError: signVesselError, error: signVesselErrorData } = useSignVesselNominationAgreement();
  const { signAgreement: signCharterPartyAgreement, isLoading: signCharterPartyLoading, isSuccess: signCharterPartySuccess, isError: signCharterPartyError, error: signCharterPartyErrorData } = useSignCharterPartyAgreement();

  // Handle success and error states
  useEffect(() => {
    if (responseSuccess) {
      showToast("Charter response sent successfully!", "success");
      setShowResponseModal(false);
      resetResponseForm();
    }
  }, [responseSuccess, showToast]);

  useEffect(() => {
    if (responseError && responseErrorData) {
      showToast(responseErrorData.message || "Failed to send charter response", "error");
    }
  }, [responseError, responseErrorData, showToast]);

  useEffect(() => {
    if (meetingDateSuccess) {
      showToast("Meeting dates selected successfully!", "success");
      setShowMeetingDateModal(false);
      setMeetingDates({ date1: "", date2: "" });
    }
  }, [meetingDateSuccess, showToast]);

  useEffect(() => {
    if (meetingDateError && meetingDateErrorData) {
      showToast(meetingDateErrorData.message || "Failed to select meeting dates", "error");
    }
  }, [meetingDateError, meetingDateErrorData, showToast]);

  useEffect(() => {
    if (signVesselSuccess) {
      showToast("Vessel nomination agreement signed successfully!", "success");
      setShowSignVesselModal(false);
    }
  }, [signVesselSuccess, showToast]);

  useEffect(() => {
    if (signVesselError && signVesselErrorData) {
      showToast(signVesselErrorData.message || "Failed to sign vessel agreement", "error");
    }
  }, [signVesselError, signVesselErrorData, showToast]);

  useEffect(() => {
    if (signCharterPartySuccess) {
      showToast("Charter party agreement signed successfully!", "success");
      setShowSignCharterPartyModal(false);
    }
  }, [signCharterPartySuccess, showToast]);

  useEffect(() => {
    if (signCharterPartyError && signCharterPartyErrorData) {
      showToast(signCharterPartyErrorData.message || "Failed to sign charter party agreement", "error");
    }
  }, [signCharterPartyError, signCharterPartyErrorData, showToast]);

  // Helper functions
  const resetResponseForm = () => {
    setResponseForm({
      canProvideSecurity: {
        processed: true,
        pricing: 0
      },
      vesselOwnerResponse: {
        processed: true,
        userMessage: ""
      }
    });
  };

  const getStatusClass = (status) => {
    switch(status) {
      case "pending": return "pending";
      case "approved": return "approved";
      case "in-use": return "in-use";
      case "declined": return "declined";
      case "In Review": return "in-review";
      case "Pending": return "pending";
      case "Approved": return "approved";
      case "Waiting for Vessel Owner Response": return "in-review";
      case "Charter Request Created": return "pending";
      case "Admin Processing": return "in-review";
      case "Vessel Owner Response Required": return "in-review";
      case "Admin Review": return "in-review";
      case "Charterer Decision": return "in-review";
      case "Select Meeting Dates": return "approved";
      case "Meeting Dates Saved": return "approved";
      case "Sign Vessel Nomination": return "approved";
      case "Processing Inspection": return "in-review";
      case "Sign Charter Party": return "approved";
      case "Completed": return "approved";
      default: return "pending";
    }
  };

  // Determine status based on step and other fields
  const getRequestStatus = (charterRequest) => {
    if (!charterRequest) return "Pending";
    
    const step = charterRequest.step;
    
    switch (step) {
      case 1:
        return "Charter Request Created"; // Charterer created request
      case 2:
        return "Admin Processing"; // Admin processing charter request
      case 3:
        return "Vessel Owner Response Required"; // Vessel owner needs to respond
      case 4:
        return "Admin Review"; // Admin reviewing vessel owner response
      case 5:
        return "Charterer Decision"; // Charterer deciding
      case 6:
        return "Admin Processing"; // Admin processing charterer decision
      case 7:
        return "Select Meeting Dates"; // Both select meeting dates
      case 8:
        return "Meeting Dates Saved"; // Meeting dates saved to database
      case 9:
        return "Sign Vessel Nomination"; // Both sign vessel nomination agreement
      case 10:
        return "Processing Inspection"; // Admin processing vessel inspection
      case 11:
        return "Sign Charter Party"; // Both sign charter party agreement
      case 12:
        return "Completed"; // All steps completed
      default:
        return "Pending";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return "Just now";
    if (diffInHours === 1) return "1 hour ago";
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "1 day ago";
    return `${diffInDays} days ago`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Action handlers
  const submitCharterResponse = () => {
    if (requestId) {
      // Prepare the body according to the API format
      const requestBody = {
        canProvideSecurity: responseForm.canProvideSecurity.processed 
          ? {
              processed: true,
              pricing: responseForm.canProvideSecurity.pricing === "" ? 0 : parseInt(responseForm.canProvideSecurity.pricing) || 0
            }
          : false,
        vesselOwnerResponse: responseForm.vesselOwnerResponse.processed
          ? {
              processed: true
            }
          : {
              processed: false,
              userMessage: responseForm.vesselOwnerResponse.userMessage
            }
      };

      sendCharterResponse({
        requestId: requestId,
        body: requestBody
      });
    }
  };

  const submitMeetingDates = () => {
    if (!meetingDates.date1 || !meetingDates.date2) {
      showToast("Please select both meeting dates", "error");
      return;
    }

    if (requestId) {
      selectMeetingDate({
        requestId: requestId,
        body: {
          date1: new Date(meetingDates.date1).toISOString(),
          date2: new Date(meetingDates.date2).toISOString()
        }
      });
    }
  };

  const confirmSignVesselAgreement = () => {
    if (requestId) {
      signVesselAgreement({
        requestId: requestId,
        body: { signed: true }
      });
    }
  };

  const confirmSignCharterPartyAgreement = () => {
    if (requestId) {
      signCharterPartyAgreement({
        requestId: requestId,
        body: { signed: true }
      });
    }
  };

  // Calculate current step and next action
  const currentStep = charterRequest ? calculateWorkflowStep(charterRequest) : 0;
  const nextAction = getNextAction(currentStep, "vesselOwner");

  // Map API step to display step (12-step workflow)
  const getDisplayStep = (apiStep) => {
    switch (apiStep) {
      case 1: return 1; // charterRequest (charterer)
      case 2: return 3; // charterResponse (vessel owner) - API step 2 means admin approved, vessel owner can respond
      case 3: return 4; // processCharterResponse (admin)
      case 4: return 5; // isCharterMovingForward (charterer)
      case 5: return 6; // processCharterMovingForward (admin) - acceptPrice false
      case 6: return 7; // selectMeetingDate (charterer and vessel owner)
      case 7: return 8; // meeting result saved (system)
      case 8: return 9; // signVesselNominationAgreement (charterer and vessel owner)
      case 9: return 10; // processVesselInspectionResult (admin)
      case 10: return 11; // signCharterPartyAgreement (charterer and vessel owner)
      case 11: return 11; // signCharterPartyAgreement (stay at 11 to show button)
      case 12: return 12; // processCharterPartyAgreement (admin)
      default: return apiStep;
    }
  };

  const displayStep = getDisplayStep(currentStep);

  // Debug logging
  console.log("Charter Request:", charterRequest);
  console.log("Current Step:", currentStep);
  console.log("Display Step:", displayStep);
  console.log("Next Action:", nextAction);
  console.log("Modal States - showSignVesselModal:", showSignVesselModal, "showSignCharterPartyModal:", showSignCharterPartyModal);
  console.log("Vessel Inspection Data:", charterRequest?.vesselInspected);
  console.log("API Step:", charterRequest?.step);
  console.log("Vessel Inspection Completed:", charterRequest?.vesselInspectionCompleted);
  console.log("Inspection Result:", charterRequest?.inspectionResult);
  console.log("Should show charter party button:", displayStep === 10 || displayStep === 11);

  if (charterRequestLoading) {
    return (
      <div className="charter-details-container">
        <div className="charter-details-header">
          <Skeleton height={40} width={200} />
        </div>
        <div className="charter-details-content">
          <div className="charter-details-main">
            <Skeleton height={300} style={{ marginBottom: 20 }} />
            <Skeleton height={20} count={8} style={{ marginBottom: 10 }} />
          </div>
          <div className="charter-details-sidebar">
            <Skeleton height={200} style={{ marginBottom: 20 }} />
            <Skeleton height={40} count={4} style={{ marginBottom: 10 }} />
          </div>
        </div>
      </div>
    );
  }

  if (charterRequestError) {
    return (
      <div className="charter-details-container">
        <div className="charter-details-header">
          <button onClick={() => navigate('/dashboard/charter-requests')} className="back-button">
            <FaArrowLeft /> Back to Charter Requests
          </button>
        </div>
        <div style={{ textAlign: "center", color: "#e74c3c", padding: "40px 0" }}>
          Error: {charterRequestError.message}
        </div>
      </div>
    );
  }

  if (!charterRequest) {
    return (
      <div className="charter-details-container">
        <div className="charter-details-header">
          <button onClick={() => navigate('/dashboard/charter-requests')} className="back-button">
            <FaArrowLeft /> Back to Charter Requests
          </button>
        </div>
        <div style={{ textAlign: "center", color: "#6b7280", padding: "40px 0" }}>
          Charter request not found
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="charter-details-container">
        {/* Header */}
        <div className="charter-details-header">
          <button onClick={() => navigate('/dashboard/charter-requests')} className="back-button">
            <FaArrowLeft /> Back to Charter Requests
          </button>
          <div className="charter-details-title">
            <h1>Charter Request #{charterRequest._id?.slice(-8)}</h1>
            <span className={`status-badge ${getStatusClass(getRequestStatus(charterRequest))}`}>
              {getRequestStatus(charterRequest)}
            </span>
          </div>
        </div>

        <div className="charter-details-content">
          {/* Main Content */}
          <div className="charter-details-main">
            {/* Workflow Steps */}
            {charterRequest && (
              <div className="detail-card">
                <WorkflowSteps 
                  currentStep={calculateWorkflowStep(charterRequest)} 
                  userType="vesselOwner" 
                />
              </div>
            )}
            
            {/* Overview Card */}
            <div className="detail-card">
              <h2>Request Overview</h2>
              <div className="overview-grid">
                <div className="overview-item">
                  <FaClock className="overview-icon" />
                  <div>
                    <label>Start Date</label>
                    <span>{charterRequest.startDate ? formatDate(charterRequest.startDate) : "N/A"}</span>
                  </div>
                </div>
                <div className="overview-item">
                  <FaCalendar className="overview-icon" />
                  <div>
                    <label>Duration</label>
                    <span>{charterRequest.duration} days</span>
                  </div>
                </div>
                <div className="overview-item">
                  <FaUsers className="overview-icon" />
                  <div>
                    <label>Passengers</label>
                    <span>{charterRequest.numberOfPassengers || "N/A"}</span>
                  </div>
                </div>
                <div className="overview-item">
                  <FaShieldAlt className="overview-icon" />
                  <div>
                    <label>Security Required</label>
                    <span>{charterRequest.security ? "Yes" : "No"}</span>
                  </div>
                </div>
                <div className="overview-item">
                  <FaDollarSign className="overview-icon" />
                  <div>
                    <label>Requested Price</label>
                    <span>{formatPrice(charterRequest.pricing)}</span>
                  </div>
                </div>
                <div className="overview-item">
                  <FaDollarSign className="overview-icon" />
                  <div>
                    <label>Final Proposed Price</label>
                    <span>{formatPrice(charterRequest.finalProposedPrice)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scope of Work */}
            <div className="detail-card">
              <h2>Scope of Work</h2>
              <p className="scope-text">{charterRequest.scopeOfWork || "No scope of work specified"}</p>
            </div>

            {/* Technical Requirements */}
            <div className="detail-card">
              <h2>Technical Requirements</h2>
              <p className="requirements-text">{charterRequest.technicalRequirements || "No technical requirements specified"}</p>
            </div>

            {/* Other Requirements */}
            <div className="detail-card">
              <h2>Other Requirements</h2>
              <p className="requirements-text">{charterRequest.otherRequirements || "No other requirements specified"}</p>
            </div>

            {/* Restrictions */}
            <div className="detail-card">
              <h2>Restrictions</h2>
              <p className="requirements-text">{charterRequest.restrictionsDraft || "No restrictions specified"}</p>
            </div>

            {/* Location Information */}
            <div className="detail-card">
              <h2>Location Information</h2>
              <div className="location-info">
                {charterRequest.origin && charterRequest.destination ? (
                  <>
                    <div className="location-item">
                      <FaMapMarkerAlt className="location-icon" />
                      <div>
                        <label>Origin</label>
                        <span>{charterRequest.origin.coordinates?.join(", ") || "N/A"}</span>
                      </div>
                    </div>
                    <div className="location-item">
                      <FaMapMarkerAlt className="location-icon" />
                      <div>
                        <label>Destination</label>
                        <span>{charterRequest.destination.coordinates?.join(", ") || "N/A"}</span>
                      </div>
                    </div>
                  </>
                ) : charterRequest.locations && charterRequest.locations.length > 0 ? (
                  <div className="location-item">
                    <FaMapMarkerAlt className="location-icon" />
                    <div>
                      <label>Locations</label>
                      <span>{charterRequest.locations.map(loc => loc.coordinates?.join(", ")).join("; ")}</span>
                    </div>
                  </div>
                ) : (
                  <p>No location information available</p>
                )}
              </div>
            </div>

            {/* Request Details */}
            <div className="detail-card">
              <h2>Request Details</h2>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Extension Period Notice</label>
                  <span>{charterRequest.extensionPeriodNotice || "N/A"} days</span>
                </div>
                <div className="detail-item">
                  <label>Created</label>
                  <span>{charterRequest.createdAt ? formatTimeAgo(charterRequest.createdAt) : "N/A"}</span>
                </div>
                <div className="detail-item">
                  <label>Security Pricing</label>
                  <span>{charterRequest.canProvideSecurity?.pricing ? formatPrice(charterRequest.canProvideSecurity.pricing) : "N/A"}</span>
                </div>
                <div className="detail-item">
                  <label>Request ID</label>
                  <span>{charterRequest._id}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="charter-details-sidebar">
            {/* Action Buttons */}
            <div className="action-card">
              <h3>Actions</h3>
              <div className="action-buttons">
                {displayStep === 3 && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Send Response</h4>
                      <p>Provide your pricing and terms for this charter request</p>
                    </div>
                    <button 
                      className="action-btn primary"
                      onClick={() => setShowResponseModal(true)}
                      disabled={responseLoading}
                    >
                      <FaCheck /> Send Response
                    </button>
                  </div>
                )}
                {displayStep === 7 && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Select Meeting Date</h4>
                      <p>Choose meeting dates with the charterer</p>
                    </div>
                    <button 
                      className="action-btn primary"
                      onClick={() => setShowMeetingDateModal(true)}
                      disabled={meetingDateLoading}
                    >
                      <FaCalendar /> Select Meeting Date
                    </button>
                  </div>
                )}
                {displayStep === 9 && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Sign Vessel Nomination Agreement</h4>
                      <p>Sign the vessel nomination agreement to proceed</p>
                    </div>
                    <button 
                      className="action-btn primary"
                      onClick={() => setShowSignVesselModal(true)}
                      disabled={signVesselLoading}
                    >
                      <FaFileSignature /> Sign Vessel Nomination Agreement
                    </button>
                  </div>
                )}
                {(displayStep === 10 || displayStep === 11) && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Sign Charter Party Agreement</h4>
                      <p>Sign the charter party agreement to complete the process</p>
                    </div>
                    <button 
                      className="action-btn primary"
                      onClick={() => setShowSignCharterPartyModal(true)}
                      disabled={signCharterPartyLoading}
                    >
                      <FaFileSignature /> Sign Charter Party Agreement
                    </button>
                  </div>
                )}
                {displayStep === 1 && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Charter Request Created</h4>
                      <p>Your charter request has been successfully created and is being processed</p>
                    </div>
                    <div className="no-action-message">
                      <p>Charter request created</p>
                    </div>
                  </div>
                )}
                {displayStep === 2 && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Under Review</h4>
                      <p>Your charter request is being reviewed by our admin team</p>
                    </div>
                    <div className="no-action-message">
                      <p>Waiting for admin processing</p>
                    </div>
                  </div>
                )}
                {displayStep === 4 && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Response Submitted</h4>
                      <p>Your response has been sent to the charterer for their decision</p>
                    </div>
                    <div className="no-action-message">
                      <p>Waiting for charterer decision</p>
                    </div>
                  </div>
                )}
                {displayStep === 5 && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Charterer Decision</h4>
                      <p>The charterer is reviewing your response and will decide whether to proceed</p>
                    </div>
                    <div className="no-action-message">
                      <p>Waiting for charterer decision</p>
                    </div>
                  </div>
                )}
                {displayStep === 6 && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Admin Processing</h4>
                      <p>Our admin team is processing the charterer's decision</p>
                    </div>
                    <div className="no-action-message">
                      <p>Waiting for admin processing</p>
                    </div>
                  </div>
                )}
                {displayStep === 8 && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Meeting Scheduled</h4>
                      <p>Meeting dates have been successfully saved and scheduled</p>
                    </div>
                    <div className="no-action-message">
                      <p>Meeting dates saved to database</p>
                    </div>
                  </div>
                )}
                {displayStep === 12 && (
                  <div className="action-item">
                    <div className="action-info">
                      <h4>Process Completed</h4>
                      <p>All agreements have been signed and the charter request is now complete!</p>
                    </div>
                    <div className="no-action-message">
                      <p>✓ Process Complete</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status Information */}
            {displayStep !== 3 && displayStep !== 7 && displayStep !== 9 && displayStep !== 10 && displayStep !== 11 && (
              <div className="action-card">
                <h3>Status</h3>
                <div className="status-info">
                  <h4>
                    {displayStep === 1 ? "Charter Request Created" :
                     displayStep === 2 ? "Admin Processing" :
                     displayStep === 4 ? "Admin Review" :
                     displayStep === 5 ? "Charterer Decision" :
                     displayStep === 6 ? "Admin Processing" :
                     displayStep === 8 ? "Meeting Dates Saved" :
                     displayStep === 11 ? "Sign Charter Party" :
                     displayStep === 12 ? "Completed" :
                     "Processing Request"}
                  </h4>
                  <p>
                    {displayStep === 1 ? "Charterer has created the charter request" :
                     displayStep === 2 ? "Admin is processing the charter request" :
                     displayStep === 4 ? "Admin is reviewing your response" :
                     displayStep === 5 ? "Charterer is deciding whether to proceed" :
                     displayStep === 6 ? "Admin is processing charterer's decision" :
                     displayStep === 8 ? "Meeting dates have been saved to database" :
                     displayStep === 11 ? "Both parties need to sign the charter party agreement" :
                     displayStep === 12 ? "All steps completed" :
                     "Request is being processed"}
                  </p>
                </div>
              </div>
            )}

            {/* Quick Info */}
            <div className="info-card">
              <h3>Quick Info</h3>
              <div className="info-item">
                <label>Request ID</label>
                <span>{charterRequest._id?.slice(-8)}</span>
              </div>
              <div className="info-item">
                <label>Status</label>
                <span className={`status-text ${getStatusClass(getRequestStatus(charterRequest))}`}>
                  {getRequestStatus(charterRequest)}
                </span>
              </div>
              <div className="info-item">
                <label>Vessel ID</label>
                <span>{charterRequest.vessel || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Vessel Owner</label>
                <span>{charterRequest.vesselOwner || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Duration</label>
                <span>{charterRequest.duration} days</span>
              </div>
              <div className="info-item">
                <label>Passengers</label>
                <span>{charterRequest.numberOfPassengers || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Security</label>
                <span>{charterRequest.security ? "Required" : "Not Required"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Charter Response Modal */}
      {showResponseModal && (
        <div className="charter-details-modal-overlay" onClick={() => setShowResponseModal(false)}>
          <div className="charter-details-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="charter-details-modal-header">
              <h3>Send Charter Response</h3>
              <p className="charter-response-modal-description">Provide your response to this charter request including pricing and terms</p>
              <button onClick={() => setShowResponseModal(false)}>&times;</button>
            </div>
            <div className="charter-details-modal-body">
              
              {/* Security Services Section */}
              <div className="charter-response-form-section">
                <div className="charter-response-section-header">
                  <h4>Security Services</h4>
                  <p className="charter-response-section-description">Indicate if you can provide security services for this charter</p>
                </div>
                
                <div className="charter-response-option-card">
                  <div className="charter-response-option-header">
                    <input
                      type="checkbox"
                      id="canProvideSecurity"
                      checked={responseForm.canProvideSecurity.processed}
                      onChange={(e) => setResponseForm(prev => ({
                        ...prev,
                        canProvideSecurity: {
                          ...prev.canProvideSecurity,
                          processed: e.target.checked
                        }
                      }))}
                    />
                    <label htmlFor="canProvideSecurity" className="charter-response-option-label">
                      <span className="charter-response-option-title">I can provide security services</span>
                      <span className="charter-response-option-subtitle">Check this if you can offer security personnel for this charter</span>
                    </label>
                  </div>
                  
                  {responseForm.canProvideSecurity.processed && (
                    <div className="charter-response-conditional-input">
                      <label htmlFor="securityPricing" className="charter-response-input-label">
                        Security Service Pricing (USD per day)
                      </label>
                      <input
                        id="securityPricing"
                        type="number"
                        placeholder="Enter daily security service cost"
                        value={responseForm.canProvideSecurity.pricing || ""}
                        onChange={(e) => setResponseForm(prev => ({
                          ...prev,
                          canProvideSecurity: {
                            ...prev.canProvideSecurity,
                            pricing: parseInt(e.target.value) || ""
                          }
                        }))}
                      />
                      <p className="charter-response-input-help">Enter the daily cost for providing security services</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Charter Request Decision Section */}
              <div className="charter-response-form-section">
                <div className="charter-response-section-header">
                  <h4>Charter Request Decision</h4>
                  <p className="charter-response-section-description">Decide whether to accept or decline this charter request</p>
                </div>
                
                <div className="charter-response-option-card">
                  <div className="charter-response-option-header">
                    <input
                      type="checkbox"
                      id="processRequest"
                      checked={responseForm.vesselOwnerResponse.processed}
                      onChange={(e) => setResponseForm(prev => ({
                        ...prev,
                        vesselOwnerResponse: {
                          ...prev.vesselOwnerResponse,
                          processed: e.target.checked,
                          userMessage: e.target.checked ? "" : prev.vesselOwnerResponse.userMessage
                        }
                      }))}
                    />
                    <label htmlFor="processRequest" className="charter-response-option-label">
                      <span className="charter-response-option-title">Accept this charter request</span>
                      <span className="charter-response-option-subtitle">I agree to proceed with this charter arrangement</span>
                    </label>
                  </div>
                </div>
                
                {!responseForm.vesselOwnerResponse.processed && (
                  <div className="charter-response-decline-section">
                    <div className="charter-response-decline-header">
                      <h5>Decline Charter Request</h5>
                      <p>Please provide a reason for declining this charter request</p>
                    </div>
                    <div className="charter-response-input-group">
                      <label htmlFor="declineReason" className="charter-response-input-label">
                        Reason for Declining
                      </label>
                      <textarea
                        id="declineReason"
                        placeholder="Please explain why you cannot accept this charter request..."
                        value={responseForm.vesselOwnerResponse.userMessage}
                        onChange={(e) => setResponseForm(prev => ({
                          ...prev,
                          vesselOwnerResponse: {
                            ...prev.vesselOwnerResponse,
                            userMessage: e.target.value
                          }
                        }))}
                        rows={4}
                      />
                      <p className="charter-response-input-help">Provide a clear explanation for declining the request</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="charter-details-modal-actions">
                <button 
                  className="charter-response-cancel-btn"
                  onClick={() => setShowResponseModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="charter-response-submit-btn"
                  onClick={submitCharterResponse}
                  disabled={responseLoading}
                >
                  {responseLoading ? "Sending Response..." : "Send Response"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meeting Date Modal */}
      {showMeetingDateModal && (
        <div className="charter-details-modal-overlay" onClick={() => setShowMeetingDateModal(false)}>
          <div className="charter-details-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="charter-details-modal-header">
              <h3>Select Meeting Dates</h3>
              <button onClick={() => setShowMeetingDateModal(false)}>&times;</button>
            </div>
            <div className="charter-details-modal-body">
              <p style={{ marginBottom: "20px", color: "#6B7280", fontSize: "14px" }}>
                Please select 2 preferred meeting dates from the available options below.
              </p>
              
              {charterRequest?.meetingDatesCreated ? (
                <>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={meetingDates.date1 === charterRequest.meetingDatesCreated.date1 || meetingDates.date2 === charterRequest.meetingDatesCreated.date1}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // If date1 is empty, use it; otherwise use date2
                            if (!meetingDates.date1) {
                              setMeetingDates(prev => ({ ...prev, date1: charterRequest.meetingDatesCreated.date1 }));
                            } else if (!meetingDates.date2) {
                              setMeetingDates(prev => ({ ...prev, date2: charterRequest.meetingDatesCreated.date1 }));
                            }
                          } else {
                            // Remove from whichever slot it's in
                            if (meetingDates.date1 === charterRequest.meetingDatesCreated.date1) {
                              setMeetingDates(prev => ({ ...prev, date1: "" }));
                            } else if (meetingDates.date2 === charterRequest.meetingDatesCreated.date1) {
                              setMeetingDates(prev => ({ ...prev, date2: "" }));
                            }
                          }
                        }}
                        disabled={(meetingDates.date1 && meetingDates.date2) && 
                                 (meetingDates.date1 !== charterRequest.meetingDatesCreated.date1 && 
                                  meetingDates.date2 !== charterRequest.meetingDatesCreated.date1)}
                      />
                      First Meeting Date: {new Date(charterRequest.meetingDatesCreated.date1).toLocaleString()}
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={meetingDates.date1 === charterRequest.meetingDatesCreated.date2 || meetingDates.date2 === charterRequest.meetingDatesCreated.date2}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // If date1 is empty, use it; otherwise use date2
                            if (!meetingDates.date1) {
                              setMeetingDates(prev => ({ ...prev, date1: charterRequest.meetingDatesCreated.date2 }));
                            } else if (!meetingDates.date2) {
                              setMeetingDates(prev => ({ ...prev, date2: charterRequest.meetingDatesCreated.date2 }));
                            }
                          } else {
                            // Remove from whichever slot it's in
                            if (meetingDates.date1 === charterRequest.meetingDatesCreated.date2) {
                              setMeetingDates(prev => ({ ...prev, date1: "" }));
                            } else if (meetingDates.date2 === charterRequest.meetingDatesCreated.date2) {
                              setMeetingDates(prev => ({ ...prev, date2: "" }));
                            }
                          }
                        }}
                        disabled={(meetingDates.date1 && meetingDates.date2) && 
                                 (meetingDates.date1 !== charterRequest.meetingDatesCreated.date2 && 
                                  meetingDates.date2 !== charterRequest.meetingDatesCreated.date2)}
                      />
                      Second Meeting Date: {new Date(charterRequest.meetingDatesCreated.date2).toLocaleString()}
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={meetingDates.date1 === charterRequest.meetingDatesCreated.date3 || meetingDates.date2 === charterRequest.meetingDatesCreated.date3}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // If date1 is empty, use it; otherwise use date2
                            if (!meetingDates.date1) {
                              setMeetingDates(prev => ({ ...prev, date1: charterRequest.meetingDatesCreated.date3 }));
                            } else if (!meetingDates.date2) {
                              setMeetingDates(prev => ({ ...prev, date2: charterRequest.meetingDatesCreated.date3 }));
                            }
                          } else {
                            // Remove from whichever slot it's in
                            if (meetingDates.date1 === charterRequest.meetingDatesCreated.date3) {
                              setMeetingDates(prev => ({ ...prev, date1: "" }));
                            } else if (meetingDates.date2 === charterRequest.meetingDatesCreated.date3) {
                              setMeetingDates(prev => ({ ...prev, date2: "" }));
                            }
                          }
                        }}
                        disabled={(meetingDates.date1 && meetingDates.date2) && 
                                 (meetingDates.date1 !== charterRequest.meetingDatesCreated.date3 && 
                                  meetingDates.date2 !== charterRequest.meetingDatesCreated.date3)}
                      />
                      Third Meeting Date: {new Date(charterRequest.meetingDatesCreated.date3).toLocaleString()}
                    </label>
                  </div>
                </>
              ) : (
                <div style={{ color: "#e74c3c", textAlign: "center", padding: "20px 0" }}>
                  No meeting dates available. Please wait for the admin to create meeting dates.
                </div>
              )}

              <div className="charter-details-modal-actions">
                <button onClick={() => setShowMeetingDateModal(false)}>Cancel</button>
                <button
                  onClick={submitMeetingDates}
                  disabled={meetingDateLoading || !meetingDates.date1 || !meetingDates.date2 || !charterRequest?.meetingDatesCreated}
                  style={{ background: "#2563EB" }}
                >
                  {meetingDateLoading ? "Submitting..." : "Submit Dates"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sign Vessel Agreement Modal */}
      <VesselNominationAgreementModal
        isOpen={showSignVesselModal}
        onClose={() => setShowSignVesselModal(false)}
        onSign={confirmSignVesselAgreement}
        isLoading={signVesselLoading}
        charterRequest={charterRequest}
        profileData={profileData}
      />

      {/* Sign Charter Party Agreement Modal */}
      <CharterPartyAgreementModal
        isOpen={showSignCharterPartyModal}
        onClose={() => setShowSignCharterPartyModal(false)}
        onSign={confirmSignCharterPartyAgreement}
        isLoading={signCharterPartyLoading}
        charterRequest={charterRequest}
        profileData={profileData}
      />
    </>
  );
};

export default CharterRequestDetails; 